/**
 * useExecuteAction — TanStack ``useMutation`` wrapping the BE
 * ``POST /api/v1/actions/{proposal_id}/execute`` endpoint.
 *
 * Sibling of ``useSendEmail`` / ``useArchiveEmail`` in
 * ``features/sources/hooks/useEmailSendMutations.ts`` — same pattern,
 * same library (TanStack Query). The mutation is intentionally thin:
 * the BE handles audit-logging (``proposed_fields`` vs ``executed_fields``
 * diff per ``feedback_hitl_card_editable_fields.md``) and emits the
 * follow-up ``action_status`` WS frame that drives the drawer's
 * ``executing → executed | error`` state machine. The hook is responsible
 * only for triggering execute + rolling back the OPTIMISTIC chip / drawer
 * state when the REST call itself fails (network drop, 4xx).
 *
 * Why not call ``apiClient.post`` directly inside ``handleConfirm``?
 * TanStack gives us ``isPending`` for disabling the Conferma button +
 * a single error surface for the toast — keeping the consumer
 * (``ActionFormSurface``) declarative.
 */

import { useMutation } from "@tanstack/react-query";
import {
  executeAction,
  type ExecuteActionRequest,
  type ExecuteActionResponse,
} from "@ando/core";

export interface ExecuteActionInput {
  /** Server-issued proposal id (UUID). */
  readonly proposalId: string;
  /** Operator-edited field values (post-AI proposal). */
  readonly modifications?: Readonly<Record<string, unknown>>;
}

export function useExecuteAction() {
  return useMutation<ExecuteActionResponse, Error, ExecuteActionInput>({
    mutationFn: ({ proposalId, modifications }) => {
      const body: ExecuteActionRequest = modifications
        ? { modifications }
        : {};
      return executeAction(proposalId, body);
    },
  });
}
